import { serverSidePagePrepare } from "@lib/hooks"
import { observer } from "mobx-react"
import {
  DocumentTemplate,
  Header,
  Page,
  PureLogo,
  useStore,
} from "@features/common"
import { RouterLink } from "@lib/routing"
import { scrollToTop } from "@lib/scroll"
import { IconHamburger } from "@lib/icons"
import { dateFormater } from "@lib/date"
import {
  DesktopTableOfContents,
  LegalSectionView,
  PreviousVersions,
  TouchTableOfContents,
} from "@features/terms"
import { SecurityLogo } from "@features/security"
import { redirect } from "@lib/isomorphic"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"

export const getServerSideProps = serverSidePagePrepare({
  callback: async ({ Models, ctx }) => {
    const { effective_date } = ctx.query
    const queryDate = Array.isArray(effective_date)
      ? effective_date[0]
      : effective_date

    await Models.FaqModel.init()
    try {
      await Models.LegalModel.getColoradoSafetyPageData(queryDate)
    } catch {
      redirect(`/${Models.GlobalModel.lang}/not-found`, ctx)
    }
  },
})

const ColoradoSafetyPolicyPage = observer(function ColoradoSafetyPolicyPage() {
  const {
    GlobalModel: { openMenu },
    LegalModel: {
      coloradoSafetySections,
      coloradoSafetyTitle,
      coloradoSafetyEffectiveDate,
      coloradoSafetyVersions,
    },
  } = useStore()
  const { asPath } = useRouter()
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    function scrollToAnchor() {
      const hash = asPath.split("#")[1]
      if (hash) {
        const section = document.querySelector(`[data-faq-id=${hash}]`)
        if (section) {
          section.scrollIntoView()
        }
      }
    }
    if (isMounted) {
      setTimeout(() => scrollToAnchor(), 100)
    }
  }, [isMounted, asPath])

  return (
    <Page
      meta={{
        title: "Colorado Safety Policy Information",
      }}
      header={
        <Header
          theme="dark"
          logo={
            <RouterLink
              uri="/legal/colorado-safety-policy"
              onClick={() => scrollToTop()}
              data-test-id="header__pure_logo"
              aria-label="root"
              className="mt-4 mb-3 tablet-m:mt-[20px] tablet-m:mb-[18px]"
            >
              <div className="shrink-0 flex items-stretch h-4 tablet-m:h-7">
                <PureLogo className="shrink-0 block h-full w-auto tablet:h-full text-white pr-2 tablet-m:pr-4" />
                <SecurityLogo />
              </div>
            </RouterLink>
          }
          languageSwitcher={null}
          navigationOpener={
            <button
              onClick={openMenu}
              data-test-id="header__menu_button"
              aria-label="menu"
            >
              <IconHamburger className="w-8 h-8 text-white" />
            </button>
          }
        />
      }
      seoH1={coloradoSafetyTitle}
    >
      <div className="container pt-3 pb-15 laptop:pt-8 laptop:pb-20 legal-document">
        {coloradoSafetySections.length > 1 ? (
          <TouchTableOfContents tableOfContents={coloradoSafetySections} />
        ) : null}
        <DocumentTemplate
          sidebar={
            coloradoSafetySections.length > 1 ? (
              <DesktopTableOfContents
                tableOfContents={coloradoSafetySections}
              />
            ) : null
          }
        >
          <section className="space-y-10" id="scrollContainerElement">
            {coloradoSafetySections.map((i) => (
              <LegalSectionView data={i} key={i.id} id={i.id} />
            ))}
            <div>
              <div className="pb-3">
                <strong>Effective date:</strong>{" "}
                {coloradoSafetyEffectiveDate
                  ? dateFormater(
                      new Date(coloradoSafetyEffectiveDate),
                      "MMMM yyyy",
                      "en",
                    )
                  : ""}
              </div>
              <PreviousVersions versions={coloradoSafetyVersions} />
            </div>
          </section>
        </DocumentTemplate>
      </div>
    </Page>
  )
})

export default ColoradoSafetyPolicyPage
